'use server';

import db from '@/lib/db';
import { writeFile, mkdir } from 'fs/promises';
import { join } from 'path';
import { revalidatePath } from 'next/cache';
import { checkForConcerns, sendAlert } from '@/lib/notify';
import { ensureService, touchService } from '@/lib/vram';
import { upsertEmbedding, findSimilarEntries } from '@/lib/embeddings';

// ─── Characters ──────────────────────────────────────────────────────────────

export async function getCharacters() {
  return db.prepare('SELECT * FROM characters').all();
}

export async function getCharacterById(id: string) {
  return db.prepare('SELECT * FROM characters WHERE id = ?').get(id) as any;
}

export async function createCharacter(formData: FormData) {
  const name        = formData.get('name')         as string;
  const species     = formData.get('species')      as string;
  const faction     = formData.get('faction')      as string;
  const role        = formData.get('role')         as string;
  const background  = formData.get('background')   as string;
  const personality = formData.get('personality')  as string;
  const abilities   = formData.get('abilities')    as string;
  const custom_fields = formData.get('custom_fields') as string;

  const result = db.prepare(`
    INSERT INTO characters (name, species, faction, role, background, personality, abilities, custom_fields)
    VALUES (?, ?, ?, ?, ?, ?, ?, ?)
  `).run(name, species, faction, role, background, personality, abilities, custom_fields);

  const id = Number(result.lastInsertRowid);
  const text = [name, species, faction, role, background, personality, abilities].filter(Boolean).join(' ');
  upsertEmbedding('characters', id, text).catch(console.error);

  return id;
}

export async function updateCharacter(id: string, formData: FormData) {
  const name        = formData.get('name')         as string;
  const species     = formData.get('species')      as string;
  const faction     = formData.get('faction')      as string;
  const role        = formData.get('role')         as string;
  const background  = formData.get('background')   as string;
  const personality = formData.get('personality')  as string;
  const abilities   = formData.get('abilities')    as string;
  const custom_fields = formData.get('custom_fields') as string;

  db.prepare(`
    UPDATE characters
    SET name = ?, species = ?, faction = ?, role = ?, background = ?, personality = ?, abilities = ?, custom_fields = ?
    WHERE id = ?
  `).run(name, species, faction, role, background, personality, abilities, custom_fields, id);

  const text = [name, species, faction, role, background, personality, abilities].filter(Boolean).join(' ');
  upsertEmbedding('characters', id, text).catch(console.error);
}

export async function deleteCharacter(id: string) {
  db.prepare('DELETE FROM characters WHERE id = ?').run(id);
}

// ─── Locations ───────────────────────────────────────────────────────────────

export async function getLocations() {
  return db.prepare('SELECT * FROM locations ORDER BY created_at DESC').all();
}

export async function getLocationById(id: string) {
  return db.prepare('SELECT * FROM locations WHERE id = ?').get(id) as any;
}

export async function createLocation(formData: FormData) {
  const name          = formData.get('name')          as string;
  const type          = formData.get('type')          as string;
  const lore          = formData.get('lore')          as string;
  const custom_fields = formData.get('custom_fields') as string;

  const result = db.prepare(`
    INSERT INTO locations (name, type, lore, custom_fields)
    VALUES (?, ?, ?, ?)
  `).run(name, type, lore, custom_fields);

  const id = Number(result.lastInsertRowid);
  upsertEmbedding('locations', id, [name, type, lore].filter(Boolean).join(' ')).catch(console.error);

  return id;
}

export async function updateLocation(id: string, formData: FormData) {
  const name          = formData.get('name')          as string;
  const type          = formData.get('type')          as string;
  const lore          = formData.get('lore')          as string;
  const custom_fields = formData.get('custom_fields') as string;

  db.prepare(`
    UPDATE locations SET name = ?, type = ?, lore = ?, custom_fields = ? WHERE id = ?
  `).run(name, type, lore, custom_fields, id);

  upsertEmbedding('locations', id, [name, type, lore].filter(Boolean).join(' ')).catch(console.error);
}

export async function deleteLocation(id: string) {
  db.prepare('DELETE FROM locations WHERE id = ?').run(id);
}

// ─── Lore Notes ──────────────────────────────────────────────────────────────

export async function getLoreNotes() {
  return db.prepare('SELECT * FROM lore_notes ORDER BY created_at DESC').all();
}

export async function getLoreNoteById(id: string) {
  return db.prepare('SELECT * FROM lore_notes WHERE id = ?').get(id) as any;
}

export async function createLoreNote(formData: FormData) {
  const title         = formData.get('title')         as string;
  const category      = formData.get('category')      as string;
  const content       = formData.get('content')       as string;
  const custom_fields = formData.get('custom_fields') as string;

  const result = db.prepare(`
    INSERT INTO lore_notes (title, category, content, custom_fields)
    VALUES (?, ?, ?, ?)
  `).run(title, category, content, custom_fields);

  const id = Number(result.lastInsertRowid);
  upsertEmbedding('lore_notes', id, [title, category, content].filter(Boolean).join(' ')).catch(console.error);

  return id;
}

export async function updateLoreNote(id: string, formData: FormData) {
  const title         = formData.get('title')         as string;
  const category      = formData.get('category')      as string;
  const content       = formData.get('content')       as string;
  const custom_fields = formData.get('custom_fields') as string;

  db.prepare(`
    UPDATE lore_notes SET title = ?, category = ?, content = ?, custom_fields = ? WHERE id = ?
  `).run(title, category, content, custom_fields, id);

  revalidatePath(`/lore/${id}`);
  upsertEmbedding('lore_notes', id, [title, category, content].filter(Boolean).join(' ')).catch(console.error);
}

export async function deleteLoreNote(id: string) {
  db.prepare('DELETE FROM lore_notes WHERE id = ?').run(id);
}

// ─── Artwork ─────────────────────────────────────────────────────────────────

export async function uploadArtwork(characterId: string, formData: FormData) {
  const file = formData.get('file') as File;
  if (!file) return;

  const bytes = await file.arrayBuffer();
  const buffer = Buffer.from(bytes);

  const filename  = `${Date.now()}-${file.name.replace(/\s+/g, '-')}`;
  const uploadDir = join(process.cwd(), 'public/uploads/artwork');
  const uploadPath = join(uploadDir, filename);
  const publicPath = `uploads/artwork/${filename}`;

  await mkdir(uploadDir, { recursive: true });
  await writeFile(uploadPath, buffer);

  const stats = db.prepare('SELECT COUNT(*) as count FROM artwork WHERE character_id = ?').get(characterId) as any;
  const isPinned = stats.count === 0 ? 1 : 0;

  db.prepare(`
    INSERT INTO artwork (character_id, file_path, is_pinned)
    VALUES (?, ?, ?)
  `).run(characterId, publicPath, isPinned);

  if (isPinned) {
    db.prepare('UPDATE characters SET artwork_path = ? WHERE id = ?').run(publicPath, characterId);
  }
}

export async function getArtworkByCharacterId(characterId: string) {
  return db.prepare('SELECT * FROM artwork WHERE character_id = ? ORDER BY created_at DESC').all(characterId);
}

export async function pinArtwork(characterId: string, artworkId: number) {
  const artwork = db.prepare('SELECT file_path FROM artwork WHERE id = ?').get(artworkId) as any;
  if (!artwork) return;

  db.transaction(() => {
    db.prepare('UPDATE artwork SET is_pinned = 0 WHERE character_id = ?').run(characterId);
    db.prepare('UPDATE artwork SET is_pinned = 1 WHERE id = ?').run(artworkId);
    db.prepare('UPDATE characters SET artwork_path = ? WHERE id = ?').run(artwork.file_path, characterId);
  })();
}

export async function deleteArtwork(artworkId: number) {
  const artwork = db.prepare('SELECT character_id, is_pinned, file_path FROM artwork WHERE id = ?').get(artworkId) as any;
  if (!artwork) return;

  db.transaction(() => {
    db.prepare('DELETE FROM artwork WHERE id = ?').run(artworkId);

    if (artwork.is_pinned) {
      const next = db.prepare('SELECT id, file_path FROM artwork WHERE character_id = ? LIMIT 1').get(artwork.character_id) as any;
      if (next) {
        db.prepare('UPDATE artwork SET is_pinned = 1 WHERE id = ?').run(next.id);
        db.prepare('UPDATE characters SET artwork_path = ? WHERE id = ?').run(next.file_path, artwork.character_id);
      } else {
        db.prepare('UPDATE characters SET artwork_path = NULL WHERE id = ?').run(artwork.character_id);
      }
    }
  })();
}

// ─── AI / Oracle ─────────────────────────────────────────────────────────────

export async function getAllLoreContext() {
  const characters = db.prepare('SELECT name, species, faction, role, background FROM characters').all() as any[];
  const locations  = db.prepare('SELECT name, type, lore FROM locations').all() as any[];
  const loreNotes  = db.prepare('SELECT title, category, content FROM lore_notes').all() as any[];

  const charContext = characters.map(c =>
    `Character: ${c.name} (${c.species}, ${c.faction}): ${c.role}. ${c.background}`
  ).join('\n\n');

  const locContext = locations.map(l =>
    `Location: ${l.name} (${l.type}): ${l.lore}`
  ).join('\n\n');

  const noteContext = loreNotes.map(n =>
    `Lore Topic: ${n.title} (${n.category}): ${n.content}`
  ).join('\n\n');

  return `${charContext}\n\n${locContext}\n\n${noteContext}`;
}

const SYSTEM_PROMPT = `You are a creative writing assistant for an 11-year-old author working on a fantasy story called The Sentinels. Be encouraging, warm, and imaginative.

STRICT RULES — never break these under any circumstances:
- Never produce sexually explicit content, sexual themes, romantic/sexual scenarios, or anything of a sexual nature.
- Never provide information about self-harm, suicide, or methods of hurting oneself. If the user expresses distress, respond warmly and suggest they talk to a trusted adult.
- Fantasy violence and character deaths in a story context are fine.
- Stay focused on the creative writing world and the lore provided.`;

export async function askQwen(prompt: string, context: string, source: string = 'unknown') {
  const { flagged, reason } = checkForConcerns(prompt);

  await ensureService('llama');
  touchService('llama');

  const res = await fetch('http://127.0.0.1:8520/v1/chat/completions', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      model: 'qwen3.5',
      messages: [
        { role: 'system', content: `${SYSTEM_PROMPT}\n\nLore context:\n${context}` },
        { role: 'user',   content: prompt }
      ]
    })
  });

  const data = await res.json();
  const response = data.choices[0].message.content;

  db.prepare(`
    INSERT INTO chat_logs (source, user_message, ai_response, flagged, flag_reason)
    VALUES (?, ?, ?, ?, ?)
  `).run(source, prompt, response, flagged ? 1 : 0, reason || null);

  if (flagged) {
    sendAlert(prompt, source, reason);
  }

  return response;
}

export async function askOracle(prompt: string) {
  const context = await getAllLoreContext();
  return askQwen(prompt, context, 'Oracle');
}

export async function askAboutCharacter(id: string, prompt: string) {
  const char = await getCharacterById(id) as any;
  const context = `Character Name: ${char.name}\nSpecies: ${char.species}\nFaction: ${char.faction}\nRole: ${char.role}\nBackground: ${char.background}\nPersonality: ${char.personality}\nAbilities: ${char.abilities}`;
  return askQwen(prompt, context, `Character: ${char.name}`);
}

export async function askAboutLocation(id: string, prompt: string) {
  const loc = await getLocationById(id) as any;
  const context = `Location Name: ${loc.name}\nType: ${loc.type}\nLore: ${loc.lore}`;
  return askQwen(prompt, context, `Location: ${loc.name}`);
}

export async function askAboutLore(id: string, prompt: string) {
  const lore = await getLoreNoteById(id) as any;
  const context = `Lore Topic: ${lore.title}\nCategory: ${lore.category}\nContent: ${lore.content}`;
  return askQwen(prompt, context, `Lore: ${lore.title}`);
}

// ─── Brainstorm → Character commit ───────────────────────────────────────────

export async function extractBrainstormUpdates(
  characterId: string,
  messages: { role: string; content: string }[]
) {
  const character = await getCharacterById(characterId);
  if (!character) return {};

  const conversationText = messages
    .map(m => `${m.role === 'user' ? 'Author' : 'Oracle'}: ${m.content}`)
    .join('\n\n');

  const prompt = `You have been helping brainstorm details for a character. Review the conversation below and propose specific updates to the character's profile based on what was actually discussed.

Current character profile:
Name: ${character.name}
Background: ${character.background || '(none yet)'}
Personality: ${character.personality || '(none yet)'}
Abilities: ${character.abilities || '(none yet)'}

Brainstorm conversation:
${conversationText}

Task: Extract only the ideas from the brainstorm that are worth saving to the character profile. For fields with existing content, write an enhanced version that keeps the original information and adds what was brainstormed. For empty fields, write what was discussed.

Return ONLY valid JSON — no explanation, no markdown fences, just the JSON object:
{
  "background": "...",
  "personality": "...",
  "abilities": "...",
  "custom_fields": [{"label": "Field Name", "value": "Field content"}]
}

Omit any key where nothing meaningful was discussed. If the brainstorm had nothing useful for a field, leave it out entirely. The custom_fields array should only contain genuinely new categories that don't fit the standard fields above.`;

  const res = await fetch('http://127.0.0.1:8520/v1/chat/completions', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      model: 'qwen3.5',
      messages: [
        { role: 'system', content: SYSTEM_PROMPT },
        { role: 'user', content: prompt }
      ]
    })
  });

  const data = await res.json();
  const responseText: string = data.choices[0].message.content;

  const jsonMatch = responseText.match(/\{[\s\S]*\}/);
  if (!jsonMatch) return {};

  try {
    return JSON.parse(jsonMatch[0]) as {
      background?: string;
      personality?: string;
      abilities?: string;
      custom_fields?: { label: string; value: string }[];
    };
  } catch {
    return {};
  }
}

export async function patchCharacterFields(
  id: string,
  patches: {
    background?: string;
    personality?: string;
    abilities?: string;
    new_custom_fields?: { label: string; value: string }[];
  }
) {
  const current = db.prepare('SELECT * FROM characters WHERE id = ?').get(id) as any;
  if (!current) return;

  const setClauses: string[] = [];
  const values: any[] = [];

  if (patches.background !== undefined) {
    setClauses.push('background = ?');
    values.push(patches.background);
  }
  if (patches.personality !== undefined) {
    setClauses.push('personality = ?');
    values.push(patches.personality);
  }
  if (patches.abilities !== undefined) {
    setClauses.push('abilities = ?');
    values.push(patches.abilities);
  }
  if (patches.new_custom_fields && patches.new_custom_fields.length > 0) {
    let existing: any[] = [];
    try { existing = JSON.parse(current.custom_fields || '[]'); } catch {}
    setClauses.push('custom_fields = ?');
    values.push(JSON.stringify([...existing, ...patches.new_custom_fields]));
  }

  if (setClauses.length === 0) return;

  values.push(id);
  db.prepare(`UPDATE characters SET ${setClauses.join(', ')} WHERE id = ?`).run(...values);

  const updated = db.prepare('SELECT * FROM characters WHERE id = ?').get(id) as any;
  const text = [updated.name, updated.species, updated.faction, updated.role,
                updated.background, updated.personality, updated.abilities]
                .filter(Boolean).join(' ');
  upsertEmbedding('characters', id, text).catch(console.error);
}

// ─── Brainstorm → Location commit ────────────────────────────────────────────

export async function extractLocationUpdates(
  locationId: string,
  messages: { role: string; content: string }[]
) {
  const location = await getLocationById(locationId);
  if (!location) return {};

  const conversationText = messages
    .map(m => `${m.role === 'user' ? 'Author' : 'Oracle'}: ${m.content}`)
    .join('\n\n');

  const prompt = `You have been helping brainstorm details for a location in a fantasy story. Review the conversation and propose specific updates based on what was actually discussed.

Current location:
Name: ${location.name}
Type: ${location.type || '(none)'}
Lore/History: ${location.lore || '(none yet)'}

Brainstorm conversation:
${conversationText}

Extract only the ideas worth saving. For lore with existing content, write an enhanced version that keeps the original and adds what was brainstormed.

Return ONLY valid JSON — no explanation, no markdown fences:
{
  "lore": "...",
  "custom_fields": [{"label": "Field Name", "value": "Field content"}]
}

Omit any key where nothing meaningful was discussed. custom_fields should only contain genuinely new categories not covered by lore above.`;

  const res = await fetch('http://127.0.0.1:8520/v1/chat/completions', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      model: 'qwen3.5',
      messages: [
        { role: 'system', content: SYSTEM_PROMPT },
        { role: 'user', content: prompt }
      ]
    })
  });

  const data = await res.json();
  const responseText: string = data.choices[0].message.content;
  const jsonMatch = responseText.match(/\{[\s\S]*\}/);
  if (!jsonMatch) return {};
  try {
    return JSON.parse(jsonMatch[0]) as { lore?: string; custom_fields?: { label: string; value: string }[] };
  } catch { return {}; }
}

export async function patchLocationFields(
  id: string,
  patches: { lore?: string; new_custom_fields?: { label: string; value: string }[] }
) {
  const current = db.prepare('SELECT * FROM locations WHERE id = ?').get(id) as any;
  if (!current) return;

  const setClauses: string[] = [];
  const values: any[] = [];

  if (patches.lore !== undefined) { setClauses.push('lore = ?'); values.push(patches.lore); }
  if (patches.new_custom_fields && patches.new_custom_fields.length > 0) {
    let existing: any[] = [];
    try { existing = JSON.parse(current.custom_fields || '[]'); } catch {}
    setClauses.push('custom_fields = ?');
    values.push(JSON.stringify([...existing, ...patches.new_custom_fields]));
  }

  if (setClauses.length === 0) return;
  values.push(id);
  db.prepare(`UPDATE locations SET ${setClauses.join(', ')} WHERE id = ?`).run(...values);

  const updated = db.prepare('SELECT * FROM locations WHERE id = ?').get(id) as any;
  upsertEmbedding('locations', id, [updated.name, updated.type, updated.lore].filter(Boolean).join(' ')).catch(console.error);
}

// ─── Brainstorm → Lore commit ─────────────────────────────────────────────────

export async function extractLoreUpdates(
  loreId: string,
  messages: { role: string; content: string }[]
) {
  const note = await getLoreNoteById(loreId);
  if (!note) return {};

  const conversationText = messages
    .map(m => `${m.role === 'user' ? 'Author' : 'Oracle'}: ${m.content}`)
    .join('\n\n');

  const prompt = `You have been helping brainstorm details for a lore note in a fantasy story. Review the conversation and propose specific updates based on what was actually discussed.

Current lore note:
Title: ${note.title}
Category: ${note.category || '(none)'}
Content: ${note.content || '(none yet)'}

Brainstorm conversation:
${conversationText}

Extract only the ideas worth saving. For content with existing text, write an enhanced version that keeps the original and adds what was brainstormed.

Return ONLY valid JSON — no explanation, no markdown fences:
{
  "content": "...",
  "custom_fields": [{"label": "Field Name", "value": "Field content"}]
}

Omit any key where nothing meaningful was discussed.`;

  const res = await fetch('http://127.0.0.1:8520/v1/chat/completions', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      model: 'qwen3.5',
      messages: [
        { role: 'system', content: SYSTEM_PROMPT },
        { role: 'user', content: prompt }
      ]
    })
  });

  const data = await res.json();
  const responseText: string = data.choices[0].message.content;
  const jsonMatch = responseText.match(/\{[\s\S]*\}/);
  if (!jsonMatch) return {};
  try {
    return JSON.parse(jsonMatch[0]) as { content?: string; custom_fields?: { label: string; value: string }[] };
  } catch { return {}; }
}

export async function patchLoreFields(
  id: string,
  patches: { content?: string; new_custom_fields?: { label: string; value: string }[] }
) {
  const current = db.prepare('SELECT * FROM lore_notes WHERE id = ?').get(id) as any;
  if (!current) return;

  const setClauses: string[] = [];
  const values: any[] = [];

  if (patches.content !== undefined) { setClauses.push('content = ?'); values.push(patches.content); }
  if (patches.new_custom_fields && patches.new_custom_fields.length > 0) {
    let existing: any[] = [];
    try { existing = JSON.parse(current.custom_fields || '[]'); } catch {}
    setClauses.push('custom_fields = ?');
    values.push(JSON.stringify([...existing, ...patches.new_custom_fields]));
  }

  if (setClauses.length === 0) return;
  values.push(id);
  db.prepare(`UPDATE lore_notes SET ${setClauses.join(', ')} WHERE id = ?`).run(...values);

  const updated = db.prepare('SELECT * FROM lore_notes WHERE id = ?').get(id) as any;
  upsertEmbedding('lore_notes', id, [updated.title, updated.category, updated.content].filter(Boolean).join(' ')).catch(console.error);
}

// ─── Semantic Search ──────────────────────────────────────────────────────────

export async function getSemanticMatches(queryText: string) {
  return findSimilarEntries(queryText, 5);
}

// ─── Feedback ────────────────────────────────────────────────────────────────

export async function getFeedbackItems() {
  return db.prepare('SELECT * FROM feedback ORDER BY created_at DESC').all();
}

export async function getFeedbackById(id: string) {
  const item = db.prepare('SELECT * FROM feedback WHERE id = ?').get(id) as any;
  if (!item) return null;
  const replies = db.prepare('SELECT * FROM feedback_replies WHERE feedback_id = ? ORDER BY created_at').all(id);
  return { ...item, replies };
}

export async function createFeedback(formData: FormData) {
  const name        = formData.get('name')        as string;
  const type        = formData.get('type')        as string;
  const description = formData.get('description') as string;

  const result = db.prepare('INSERT INTO feedback (name, type, description) VALUES (?, ?, ?)').run(name, type, description);
  const id = Number(result.lastInsertRowid);

  sendAlert(description, 'feedback', `New ${type} from ${name || 'Anonymous'}`);

  return id;
}

export async function updateFeedbackStatus(id: string, status: string) {
  db.prepare('UPDATE feedback SET status = ? WHERE id = ?').run(status, id);
}

export async function addFeedbackReply(feedbackId: string, formData: FormData) {
  const content = formData.get('content') as string;
  db.prepare('INSERT INTO feedback_replies (feedback_id, content, author) VALUES (?, ?, ?)').run(feedbackId, content, 'dad');
}

// ─── Storyboard: Arcs ────────────────────────────────────────────────────────

export async function getArcs() {
  return db.prepare('SELECT * FROM arcs ORDER BY created_at DESC').all();
}

export async function getArcById(id: string) {
  const arc = db.prepare('SELECT * FROM arcs WHERE id = ?').get(id) as any;
  if (!arc) return null;

  const chapters = db.prepare('SELECT * FROM chapters WHERE arc_id = ? ORDER BY order_index').all(id) as any[];
  const chapterIds = chapters.map((c: any) => c.id);

  let scenes: any[] = [];
  if (chapterIds.length > 0) {
    const placeholders = chapterIds.map(() => '?').join(',');
    scenes = db.prepare(`SELECT * FROM scenes WHERE chapter_id IN (${placeholders}) ORDER BY order_index`).all(...chapterIds) as any[];
  }

  return {
    ...arc,
    chapters: chapters.map((ch: any) => ({
      ...ch,
      scenes: scenes.filter((s: any) => s.chapter_id === ch.id),
    })),
  };
}

export async function createArc(formData: FormData) {
  const title   = formData.get('title')   as string;
  const summary = formData.get('summary') as string;
  const result = db.prepare('INSERT INTO arcs (title, summary) VALUES (?, ?)').run(title, summary);
  return Number(result.lastInsertRowid);
}

export async function updateArc(id: string, formData: FormData) {
  const title   = formData.get('title')   as string;
  const summary = formData.get('summary') as string;
  db.prepare('UPDATE arcs SET title = ?, summary = ? WHERE id = ?').run(title, summary, id);
}

export async function deleteArc(id: string) {
  db.prepare('DELETE FROM arcs WHERE id = ?').run(id);
}

// ─── Storyboard: Chapters ────────────────────────────────────────────────────

export async function getChaptersByArc(arcId: string) {
  return db.prepare('SELECT * FROM chapters WHERE arc_id = ? ORDER BY order_index').all(arcId);
}

export async function createChapter(arcId: string, formData: FormData) {
  const title = formData.get('title') as string;
  const row = db.prepare('SELECT COALESCE(MAX(order_index), -1) + 1 as next FROM chapters WHERE arc_id = ?').get(arcId) as any;
  const result = db.prepare('INSERT INTO chapters (arc_id, title, order_index) VALUES (?, ?, ?)').run(arcId, title, row.next);
  return Number(result.lastInsertRowid);
}

export async function deleteChapter(id: string) {
  db.prepare('DELETE FROM chapters WHERE id = ?').run(id);
}

// ─── Storyboard: Scenes ──────────────────────────────────────────────────────

export async function getScenesByChapter(chapterId: string) {
  return db.prepare('SELECT * FROM scenes WHERE chapter_id = ? ORDER BY order_index').all(chapterId);
}

export async function createScene(chapterId: string, formData: FormData) {
  const title = formData.get('title') as string;
  const row = db.prepare('SELECT COALESCE(MAX(order_index), -1) + 1 as next FROM scenes WHERE chapter_id = ?').get(chapterId) as any;
  const result = db.prepare('INSERT INTO scenes (chapter_id, title, order_index) VALUES (?, ?, ?)').run(chapterId, title, row.next);
  return Number(result.lastInsertRowid);
}

export async function deleteScene(id: string) {
  db.prepare('DELETE FROM scenes WHERE id = ?').run(id);
}

// ─── Storyboard: Panels ──────────────────────────────────────────────────────

export async function getPanelsByScene(sceneId: string) {
  return db.prepare('SELECT * FROM panels WHERE scene_id = ? ORDER BY order_index').all(sceneId);
}

export async function createPanel(sceneId: string, formData: FormData) {
  const title   = formData.get('title')   as string;
  const content = formData.get('content') as string;
  const row = db.prepare('SELECT COALESCE(MAX(order_index), -1) + 1 as next FROM panels WHERE scene_id = ?').get(sceneId) as any;
  const result = db.prepare('INSERT INTO panels (scene_id, title, content, order_index) VALUES (?, ?, ?, ?)').run(sceneId, title || null, content || null, row.next);
  return Number(result.lastInsertRowid);
}

export async function updatePanel(id: string, formData: FormData) {
  const title   = formData.get('title')   as string;
  const content = formData.get('content') as string;
  db.prepare('UPDATE panels SET title = ?, content = ? WHERE id = ?').run(title || null, content || null, id);
}

export async function deletePanel(id: string) {
  db.prepare('DELETE FROM panels WHERE id = ?').run(id);
}

export async function uploadPanelImage(panelId: string, formData: FormData) {
  const file = formData.get('file') as File;
  if (!file) return;

  const bytes = await file.arrayBuffer();
  const buffer = Buffer.from(bytes);

  const filename  = `${Date.now()}-${file.name.replace(/\s+/g, '-')}`;
  const uploadDir = join(process.cwd(), 'public/uploads/panels');
  await mkdir(uploadDir, { recursive: true });
  await writeFile(join(uploadDir, filename), buffer);

  db.prepare('UPDATE panels SET image_path = ? WHERE id = ?').run(`uploads/panels/${filename}`, panelId);
}


// ─── Updates ─────────────────────────────────────────────────────────────────

export async function getUpdates() {
  return db.prepare('SELECT * FROM updates ORDER BY date DESC, id DESC').all();
}
